import { CardData, CardType } from "../constants/GlobalTypesAndEnumsAndConstants";

/** Free-response questions: the learner writes the answer in (as opposed to choosing one). */
export function isWriteInQuestion(cardData: CardData | null | undefined): boolean {
  return (
    cardData?.card_type === CardType.RegularFreeResponseQuestion ||
    cardData?.card_type === CardType.CaseScenarioFreeResponseQuestion
  );
}

/**
 * The write-in questions of the set under review that "remove write-in questions" takes out, each once: the set's
 * cards are in more than one list (all of the set's cards, and the review's own order, which during a first
 * review holds only its first questions). Questions someone else owns are left alone, as the server would.
 */
export function getWriteInQuestionsOfSet({
  cardListsOfSet,
  userId,
}: {
  cardListsOfSet: Array<CardData[] | null | undefined>;
  userId: string | null | undefined;
}): CardData[] {
  const writeInQuestionsById = new Map<string, CardData>();
  for (const cardList of cardListsOfSet) {
    for (const cardData of cardList ?? []) {
      if (!cardData?.id || cardData.is_deleted || !isWriteInQuestion(cardData)) continue;
      const isOwnedBySomeoneElse = !!cardData.user_id && cardData.user_id !== userId;
      if (isOwnedBySomeoneElse) continue;
      writeInQuestionsById.set(cardData.id, cardData);
    }
  }
  return Array.from(writeInQuestionsById.values());
}

/** Whether the set keeps any question once its write-in questions are gone (else the option would empty it). */
export function doesSetHaveQuestionsOtherThanWriteIn(cardListsOfSet: Array<CardData[] | null | undefined>): boolean {
  return cardListsOfSet.some((cardList) =>
    (cardList ?? []).some((cardData) => !!cardData && !cardData.is_deleted && !isWriteInQuestion(cardData))
  );
}

/**
 * Where a review stands once some of its cards are taken out: on the same card when it stays, otherwise on the
 * first card after it that stays (the review "moves on" to the next question), otherwise on the last one left.
 * Returns 0 for a review left with no cards.
 */
export function getReviewIndexAfterRemovingCards({
  cardsBeforeRemoval,
  selectedIndexBeforeRemoval,
  removedCardIds,
}: {
  cardsBeforeRemoval: CardData[];
  selectedIndexBeforeRemoval: number;
  removedCardIds: Set<string>;
}): number {
  let numberOfRemainingCardsBeforeSelected = 0;
  for (let cardIndex = 0; cardIndex < cardsBeforeRemoval.length; cardIndex++) {
    const isRemoved = removedCardIds.has(cardsBeforeRemoval[cardIndex]?.id);
    if (cardIndex >= selectedIndexBeforeRemoval && !isRemoved) {
      // the selected card itself when it stays, or the next one that stays
      return numberOfRemainingCardsBeforeSelected;
    }
    if (!isRemoved) numberOfRemainingCardsBeforeSelected++;
  }
  // nothing stays at or after the selected card: the last card left
  return Math.max(0, numberOfRemainingCardsBeforeSelected - 1);
}
